import React from "react";
import { Row, Col, FormGroup, Label, Input, Button } from "reactstrap";

/**
 * Form header — every input above the tab strip (Pass 1 §1.4). Phase is a 3-way radio,
 * Stage is a multi-check (legacy uses radio buttons but enforces "at least one"),
 * ITAR is a 2-way radio. SO# and Quote# parse-validated server-side; we just trust the input here.
 *
 * Customer is read-only after Epicor pre-fill; user changes via the modal.
 *
 * Round 3 (UX): `errors` is the submitErrors map from the parent. When a key is
 * present, the matching field is outlined in red (is-invalid / red border) and a
 * small "required" hint is shown, so a failed Submit points at the exact field(s)
 * instead of relying on a toast the user can miss. `errors` defaults to {} so the
 * component behaves exactly as before when no validation has run.
 */
export default function HeaderFields({
    form,
    setForm,
    salesReps,
    onOpenCustomerPicker,
    readOnly,
    canEditEpicorFields,
    errors = {},
}) {
    const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

    // Helper: red-border class for an invalid <Input>.
    const inv = (key) => (errors[key] ? " is-invalid" : "");
    // Helper: red box around a fieldset (Phase / ITAR / Stage) on error.
    const fieldsetStyle = (key) =>
        errors[key]
            ? { border: "1px solid #f06548", borderRadius: "0.25rem", padding: "0.5rem 0.75rem" }
            : undefined;
    const Hint = ({ show, text }) =>
        show ? <div className="text-danger small mt-1"><i className="ri-error-warning-line me-1" />{text}</div> : null;

    return (
        <>
            <Row className="mb-3">
                <Col md={6}>
                    <FormGroup>
                        <Label className="fw-semibold">Title <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            className={inv("title")}
                            value={form.title || ""}
                            disabled={readOnly}
                            onChange={(e) => upd({ title: e.target.value })}
                            placeholder="Short descriptive title of this CR"
                        />
                        <Hint show={!!errors.title} text="Title is required." />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">SO #</Label>
                        <Input
                            type="number"
                            value={form.salesOrderNum ?? ""}
                            disabled={readOnly || !canEditEpicorFields}
                            onChange={(e) =>
                                upd({ salesOrderNum: e.target.value === "" ? null : Number(e.target.value) })
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Quote #</Label>
                        <Input
                            type="number"
                            value={form.quoteNumber ?? ""}
                            disabled={readOnly || !canEditEpicorFields}
                            onChange={(e) =>
                                upd({ quoteNumber: e.target.value === "" ? null : Number(e.target.value) })
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={5}>
                    <FormGroup>
                        <Label className="fw-semibold">Customer Name</Label>
                        <Input
                            type="text"
                            className={inv("custId")}
                            value={form.custName || ""}
                            disabled
                            readOnly
                        />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Customer ID <span className="text-danger">*</span></Label>
                        <div className="d-flex gap-2 align-items-center">
                            <Input
                                type="text"
                                className={inv("custId")}
                                value={form.custId || ""}
                                disabled
                                readOnly
                            />
                            <Button
                                type="button"
                                color={errors.custId ? "danger" : "light"}
                                outline={!!errors.custId}
                                size="sm"
                                disabled={readOnly}
                                onClick={onOpenCustomerPicker}
                                title="Pick customer from Epicor"
                            >
                                <i className="ri-search-line" />
                            </Button>
                        </div>
                        <Hint show={!!errors.custId} text="Pick a customer using the search button." />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label className="fw-semibold">Salesperson <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            className={inv("salesRep")}
                            value={form.salesRepCode || ""}
                            disabled={readOnly}
                            onChange={(e) => upd({ salesRepCode: e.target.value })}
                        >
                            <option value="">— select —</option>
                            {(salesReps || []).map((sr) => (
                                <option key={sr.salesRepCode} value={sr.salesRepCode}>
                                    {sr.name} ({sr.salesRepCode})
                                </option>
                            ))}
                        </Input>
                        <Hint show={!!errors.salesRep} text="Salesperson is required." />
                    </FormGroup>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={6}>
                    <FormGroup tag="fieldset" style={fieldsetStyle("phase")}>
                        <legend className="fw-semibold fs-6">Phase of Sale <span className="text-danger">*</span></legend>
                        <div className="d-flex gap-4">
                            <PhaseRadio label="RFQ" checked={!!form.statusRfq} disabled={readOnly} onChange={() => upd({ statusRfq: true, statusQuoted: false, statusPoReceived: false })} />
                            <PhaseRadio label="Quote" checked={!!form.statusQuoted} disabled={readOnly} onChange={() => upd({ statusRfq: false, statusQuoted: true, statusPoReceived: false })} />
                            <PhaseRadio label="PO Received" checked={!!form.statusPoReceived} disabled={readOnly} onChange={() => upd({ statusRfq: false, statusQuoted: false, statusPoReceived: true })} />
                        </div>
                        <Hint show={!!errors.phase} text="Pick a Phase of Sale." />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup tag="fieldset" style={fieldsetStyle("itar")}>
                        <legend className="fw-semibold fs-6">ITAR Applies? <span className="text-danger">*</span></legend>
                        <div className="d-flex gap-4">
                            <PhaseRadio label="Yes" checked={form.itarApply === true} disabled={readOnly} onChange={() => upd({ itarApply: true })} />
                            <PhaseRadio label="No" checked={form.itarApply === false} disabled={readOnly} onChange={() => upd({ itarApply: false })} />
                        </div>
                        <Hint show={!!errors.itar} text="Pick Yes or No." />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Complete Task By</Label>
                        <Input
                            type="date"
                            value={toIso(form.crCompletionDate)}
                            disabled={readOnly}
                            onChange={(e) => upd({ crCompletionDate: e.target.value || null })}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={12}>
                    <FormGroup tag="fieldset" style={fieldsetStyle("stage")}>
                        <legend className="fw-semibold fs-6">Stage of This Project <span className="text-danger">*</span></legend>
                        <div className="d-flex flex-wrap" style={{ columnGap: "1.75rem", rowGap: "0.6rem", marginTop: "0.5rem" }}>
                            <StageCheck label="Similar to Existing Part" k="stageSimilarToExistingPart" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="New Costing" k="stageNewCosting" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="Length Change" k="stageLengthChange" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="Initial Opportunity" k="stageInitialOpportunity" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="Lead Time" k="stageLeadTime" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="Contract Modification" k="stageContractModification" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="QA Terms" k="stageQaTerms" form={form} upd={upd} readOnly={readOnly} />
                            <StageCheck label="Other" k="stageOther" form={form} upd={upd} readOnly={readOnly} />
                        </div>
                        <Hint show={!!errors.stage} text="Pick at least one stage." />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label className="fw-semibold">Description of Post-Quote Changes</Label>
                        <Input
                            type="textarea"
                            rows={3}
                            value={form.descriptionOfPostQuoteChanges || ""}
                            disabled={readOnly}
                            onChange={(e) => upd({ descriptionOfPostQuoteChanges: e.target.value })}
                        />
                    </FormGroup>
                </Col>
            </Row>
        </>
    );
}

function PhaseRadio({ label, checked, disabled, onChange }) {
    return (
        <label className="form-check d-flex align-items-center gap-1 mb-0">
            <input type="radio" className="form-check-input" checked={checked} disabled={disabled} onChange={onChange} />
            <span>{label}</span>
        </label>
    );
}

function StageCheck({ label, k, form, upd, readOnly }) {
    // Plain flex label — deliberately NOT using Bootstrap's `.form-check`
    // class, which adds `padding-left: 1.5em` for absolutely-positioned
    // checkboxes and collapses the spacing between items in a flex-wrap row.
    return (
        <label
            className="d-inline-flex align-items-center mb-0"
            style={{
                gap: "0.45rem",
                whiteSpace: "nowrap",
                cursor: readOnly ? "default" : "pointer",
            }}
        >
            <input
                type="checkbox"
                className="form-check-input mt-0"
                style={{ margin: 0, flexShrink: 0 }}
                checked={!!form[k]}
                disabled={readOnly}
                onChange={(e) => upd({ [k]: e.target.checked })}
            />
            <span>{label}</span>
        </label>
    );
}

function toIso(d) {
    if (!d) return "";
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    try {
        const dt = new Date(d);
        if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
    } catch { /* ignore */ }
    return "";
}
